import {usePayment} from "@/hooks/usePayment";
import {settingsModalAtom} from "@/store/jotaiAtoms";
import type {TSettingsModalState} from "@/store/types";
import {stripePromise} from "@/stripe";
import {useSetAtom} from "jotai";
import {useState} from "react";
import {useEffectOnce} from "react-use";

const STORAGE_KEY = "stripeSetupReturn";

type TStripeSetupReturn = {
  agencyId?: number;
  companyId?: number;
  settingsModal: TSettingsModalState;
};

const readStripeSetupReturn = (): TStripeSetupReturn | null => {
  try {
    return JSON.parse(sessionStorage.getItem(STORAGE_KEY) ?? "");
  } catch {
    return null;
  }
};

export const rememberStripeSetupReturn = (setupReturn: TStripeSetupReturn) =>
  sessionStorage.setItem(STORAGE_KEY, JSON.stringify(setupReturn));

// Redirect based payment methods leave the app for the bank, so everything the
// setup form would have done after confirmSetup is lost. What the caller
// remembered before starting - which settings screen to reopen, whose Stripe
// customer the card belongs to - is picked up here on the way back.
const useStripeSetupReturn = () => {
  const setSettingsModal = useSetAtom(settingsModalAtom);
  const [setupReturn] = useState(readStripeSetupReturn);
  const {setDefaultPaymentMethod} = usePayment({agencyId: setupReturn?.agencyId, companyId: setupReturn?.companyId});

  useEffectOnce(() => {
    const params = new URLSearchParams(window.location.search);
    const secret = params.get("setup_intent_client_secret");

    if (!setupReturn || !secret || params.get("redirect_status") !== "succeeded") return;

    sessionStorage.removeItem(STORAGE_KEY);
    setSettingsModal(setupReturn.settingsModal);

    (async () => {
      try {
        const stripe = await stripePromise;
        const setupIntent = (await stripe?.retrieveSetupIntent(secret))?.setupIntent;

        if (typeof setupIntent?.payment_method === "string") {
          await setDefaultPaymentMethod({paymentMethodId: setupIntent.payment_method});
        }
      } finally {
        params.delete("setup_intent");
        params.delete("setup_intent_client_secret");
        params.delete("redirect_status");
        const cleaned = params.toString();
        window.history.replaceState({}, "", `${window.location.pathname}${cleaned ? `?${cleaned}` : ""}`);
      }
    })();
  });
};

export default useStripeSetupReturn;
